<script>
  import ThrowPropsPlugin from 'ThrowPropsPlugin'
  import { TweenMax, Power1 } from 'gsap'
  import { Bus, footIntroH } from '@/lib/consts'
  import { disableScroll, enableScroll } from '@/lib/funcs'
  import _ from 'lodash'
  import 'Waypoint'
  import 'ScrollToPlugin'
  import StateMixin from '@/components/report/mixins/StateMixin'

  export default {
    mixins: [ StateMixin ],
    props: {
      winHForced: [ Number, Boolean ],
      idx: { type: Number, required: true },
      keyword: { type: String, required: true },
      subhead: { type: String, required: true },
      numeral: { type: String, required: true }
    },
    data () {
      return {
        h: footIntroH,
        time: 1.25,
        stagger: 0.1,
        backPt: 80,
        frontPtLow: 110,
        frontPtHigh: 30,
        winHFix: window.innerHeight,
        allowPointer: false,
        outStartTime: 0,
        tlDur: 0,
        startY: 0,
        currY: 0,
        vTracker: null,
        active: false,
        introducing: false,
        leaving: false,
        atBottom: false,
        deskWp: null
      }
    },
    computed: {
      outDur () {
        return this.tlDur - this.outStartTime
      },
      dragProg () {
        return Math.max( 0, this.startY - this.currY ) / this.startY
      },
      willGo () {
        return (this.dragProg > 0.25)
      },
      minDur () {
        return this.willGo ? 1.5 : 0
      },
      maxDur () {
        return this.willGo ? 1.75 : 3
      },
      wrapH () {
        return this.winHForced || this.winHFix || this.winH
      },
      wrapStyles () {
        return { height: (this.wrapH + 1) + 'px', top: 'calc(100% - ' + this.wrapH + 'px)' }
      }
    },
    watch: {
      dragProg ( val ) {
        this.tl.time( this.outStartTime + this.outDur * this.dragProg )
      }
    },
    methods: {
      initTl () {
        if ( this.hasRefs() ) {
          this.setTweens()
          this.tl.pause().call( () => {
            this.introducing = true
          } )
          this.tweenIn()
          this.tl.call( () => {
            this.allowPointer = true
            this.$emit( 'introduced' )
          } )
          this.initTlOutro()
        }
      },

      initTlOutro () {
        if ( this.hasRefs() ) {
          this.tl.addLabel( 'introEnd' ).pause()
          this.tweenOut()
          this.tl.call( () => this.go() )

          this.outStartTime = this.tl.getLabelTime( 'introEnd' )
          this.tlDur = this.tl.duration()
        }
      },

      go () {
        enableScroll()
        let nextIdx = this.idx + 1
        this.$router.push( '/report/' + nextIdx )
        if ( !this.isMobile ) this.$store.dispatch( 'setSideNavActiveIdx', nextIdx )
      },

      handleClick () {
        if ( !this.leaving && this.hasRefs() ) {
          document.body.classList.remove( 'show-gnav' )
          document.body.classList.add( 'hide-gnav' )
          this.leaving = true
          disableScroll()
          this.tl.to( window, this.time * 0.9, { scrollTo: { y: 'max', autoKill: false } }, 'out' )
          this.tl.play()
        }
      },

      touchstart ( { changedTouches: { 0: { clientY: y } } } ) {
        if ( !this.leaving ) {
          this.startY = this.currY = y
        }
      },

      touchmove ( { changedTouches: { 0: { clientY: y } } } ) {
        if ( !this.leaving ) {
          this.currY = y
        }
      },

      touchend ( { changedTouches: { 0: { clientY: y } } } ) {
        if ( !this.leaving ) {
          this.allowPointer = false
          this.currY = y

          if ( this.willGo ) {
            document.body.classList.remove( 'show-gnav' )
            document.body.classList.add( 'hide-gnav' )
            this.leaving = true
            disableScroll()
            TweenMax.to( window, this.minDur * 0.9, { scrollTo: { y: 'max', autoKill: false } } )
          }

          ThrowPropsPlugin.to( this, {
            throwProps: { currY: { end: this.willGo ? 0 : this.startY } },
            ease: Power1.easeOut,
            onComplete: () => {
              this.allowPointer = true
            }
          }, this.maxDur, this.minDur, 0 )
        }
      },

      hasRefs () {
        return Object.values( this.$refs ).filter( el => typeof el === 'undefined' ).length === 0
      },

      initDeskWp () {
        let cb = e => {
          window.removeEventListener( 'wheel', cb )
          if ( e.deltaY > 0 && this.atBottom ) {
            this.handleClick()
            this.deskWp.destroy()
          }
        }
        this.deskWp = new window.Waypoint( {
          element: this.$el,
          handler: _.throttle( ( dir ) => {
            this.atBottom = (dir === 'down')
            if ( this.introducing && this.atBottom ) {
              this.$once( 'introduced', () => setTimeout( () => window.addEventListener( 'wheel', cb ), 750 ) )
            } else if ( this.atBottom ) {
              setTimeout( () => window.addEventListener( 'wheel', cb ), 750 )
            }
          }, 200 )
        } )
      }
    },
    mounted () {
      let ran = false
      Bus.$on( 'titleVectHeight', h => {
        if ( !this.leaving && this.hasRefs() ) {  // prevents erroring when user hits browser back button
          let old = this.winHFix
          this.winHFix = this.isSafari ? window.innerHeight : h

          if ( this.introducing ) {
            let { footText, bigCirc, ...setRefs } = this.$refs
            this.$once( 'introduced', () => this.$nextTick( () => {
              if ( !ran && this.hasRefs() ) {
                ran = true
                TweenMax.to( Object.values( { ...setRefs } ), this.time / 2, {
                  y: '-=' + (old - this.winHFix)
                } )
                this.tl.clear( true ).pause()
                this.initTlOutro()
              }
            } ) )
          } else if ( this.active && !this.introducing ) {
            let { footText, bigCirc, ...setRefs } = this.$refs
            TweenMax.set( Object.values( { ...setRefs } ), {
              y: '-=' + (old - this.winHFix)
            } )
            this.tl.clear( true )
            this.$nextTick( () => this.initTlOutro() )
          }
        }
      } )

      this.$on( 'introduced', () => {
        this.introducing = false
      } )

      Bus.$on( 'resized', () => {
        if ( !this.active && this.hasRefs() ) {
          this.tl.clear( true )
          this.$nextTick( () => this.initTl() )
        }
      } )
      this.$nextTick( () => this.initTl() )

      this.vTracker = ThrowPropsPlugin.track( this, 'currY' )
      this.winHFix = this.$el.clientHeight // So title SVG's behave a bit better on iOS

      setTimeout( () => {
        /* eslint-disable */
        let wp = new window.Waypoint( {
          element: this.$el,
          handler: () => {
            if ( !this.active && this.hasRefs() ) {
              this.active = true
              this.tl.tweenTo( 'introEnd' )
              wp.destroy()
            }
          },
          offset: '15%'
        } )

        if ( !this.isMobile ) this.initDeskWp()
      }, 500 )
    },
    beforeDestroy () {
      Bus.$off( 'titleVectHeight' )
    }
  }
</script>

<style lang="scss">
  @import "../../../assets/scss/global/the_goods";

  .page-footer-wrap {
    bottom : 0;

    &, .inner-footer, .footer-text, svg {
      backface-visibility : hidden;
    }

    .footer-text {
      padding-top : 2rem;
      color       : #fff;
      top         : 0;
      cursor      : pointer;

      .numeral {
        font-size   : rem(24);
        font-family : $adobe-clean-serif;
        font-weight : 700;
      }

      .keyword {
        font-size   : rem(42);
        font-family : $adobe-clean-serif;
        font-weight : 400;
      }

      .subhead {
        font-size   : rem(18);
        font-family : $adobe-clean-serif;
        font-weight : 500;
        font-style  : italic;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/report/mixins/FooterMixin.vue